import Phaser from "phaser";
import type { SceneKey } from "@/ui/dom";
import type { OverlayHandles } from "@/ui/dom";
import { artTextureKey, markArtLoaded, parseManifest, type SpriteManifest } from "@/sprites/artManifest";
import { prefetchAllPacks, registerArtHost } from "@/sprites/artPacks";

/**
 * The art preloader (RFC v0.7 §4). Runs BEFORE the office: fetches the sprite
 * manifest, queues each pack's image, and marks the ones that actually land so
 * `resolveCharacterTexture` swaps them in for the baked placeholders.
 *
 * Graceful by contract: an absent manifest (404/network) or a pack that fails
 * to load just leaves the placeholder in place — the game boots either way. A
 * present-but-malformed manifest logs LOUD (field-named) and falls back, so a
 * dev bug is unmissable without bricking a player.
 */

const MANIFEST_KEY = "art-manifest";

function assetUrl(fileRelToPublic: string): string {
  return `${import.meta.env.BASE_URL}${fileRelToPublic}`;
}

export class PreloadScene extends Phaser.Scene {
  private nextData: { overlay: OverlayHandles } | undefined;
  private onReady: (() => void) | undefined;
  private manifest: SpriteManifest | null = null;

  constructor() {
    super("preload");
  }

  init(data: { overlay: OverlayHandles; onReady?: () => void }): void {
    this.nextData = { overlay: data.overlay };
    this.onReady = data.onReady;
  }

  preload(): void {
    // A missing manifest is fine (fallback) — swallow its load error only.
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      if (file.key === MANIFEST_KEY) {
        console.info("[art] no manifest — using procedural placeholders");
      } else {
        console.warn(`[art] pack failed to load: ${file.key} — placeholder stays`);
      }
    });
    this.load.json(MANIFEST_KEY, assetUrl("assets/manifest.json"));
    this.load.once(`filecomplete-json-${MANIFEST_KEY}`, () => this.onManifest());
  }

  private onManifest(): void {
    const raw = this.cache.json.get(MANIFEST_KEY);
    try {
      this.manifest = parseManifest(raw);
    } catch (err) {
      // Malformed manifest: loud + field-named, but keep the game playable.
      console.error("[art] manifest is invalid — falling back to placeholders:", err);
      this.manifest = null;
      return;
    }
    // Queue the FIRST-PAINT packs only (v0.11 §3.2): core + office on a v2
    // manifest; a v1 manifest has no packs and loads whole, as before.
    // Entries with `frames` are horizontal spritesheets (v0.9 animation).
    for (const [key, entry] of Object.entries(this.manifest.sprites)) {
      if (this.manifest.version === 2 && entry.pack !== "core" && entry.pack !== "office") continue;
      if (entry.frames !== undefined && entry.frames > 1) {
        this.load.spritesheet(artTextureKey(key), assetUrl(entry.file), {
          frameWidth: entry.frameWidth,
          frameHeight: entry.frameHeight,
          // Pin the frame count to the manifest — Phaser otherwise slices
          // purely by image size and a padded sheet silently diverges.
          endFrame: entry.frames - 1,
        });
      } else {
        this.load.image(artTextureKey(key), assetUrl(entry.file));
      }
    }
  }

  create(): void {
    // Mark only packs whose texture is actually resident (load errors skipped).
    if (this.manifest) {
      for (const key of Object.keys(this.manifest.sprites)) {
        if (this.textures.exists(artTextureKey(key))) markArtLoaded(key);
      }
    }
    // v0.11: this scene STAYS RUNNING as the pack-load host — a slept
    // scene's Loader stalls, and the tab scenes all sleep. It renders
    // nothing, so keeping it alive is free.
    registerArtHost(this, this.manifest, ["core", "office"]);
    this.onReady?.(); // first-paint art is in — overlay navigation is safe to enable now
    this.scene.launch("office" satisfies SceneKey, this.nextData);
    prefetchAllPacks(); // idle-warm the rest, continuum order (RULED §8.4)
  }
}
